import {
  CbBorderWidth25,
  CbColorBackgroundInteractiveHovered,
  CbColorBorderBase,
  CbColorBorderInteractiveSelected,
  CbColorBorderTertiary,
  CbColorFontSubtle,
  CbFontWeightOpenSansNormal
} from '@coolblue-development/design-tokens';
import { Button, Flex, FormHelperText, Radio, ResponsiveValue } from '@coolblue-development/freddy';
import selectAnswerTrigger from '@coolblue-development/ga4-events/triggers/VacancyApplicationForm/selectAnswerTrigger';
import selectWorkingHoursTrigger from '@coolblue-development/ga4-events/triggers/VacancyApplicationForm/selectWorkingHoursTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { RadioField, Rules } from '@sharedTypes';
import { FieldValues, RegisterOptions, useFormContext } from 'react-hook-form';

import { FormLabelWithInfo } from '../FormLabelWithInfo';

type Props = {
  direction?: ResponsiveValue<'column' | 'row'>;
  name: string;
  radioFields: RadioField[];
  rules?: Rules;
  label: string;
  disable?: boolean;
  info?: string;
};

export const RadioHelper = ({
  direction,
  name,
  radioFields,
  rules,
  label,
  disable = false,
  info
}: Props) => {
  const {
    register,
    setValue,
    watch,
    trigger,
    formState: { errors }
  } = useFormContext();
  const errorMessage = errors[name]?.message;

  const { trackEvent } = useAnalytics();

  return (
    <Flex direction='column' gap={100}>
      <FormLabelWithInfo label={label} info={info} />
      <Flex direction={direction} rowGap={100} columnGap={200} wrap='wrap'>
        {radioFields.map(radioField => (
          <Button
            component='div'
            variant='tertiary'
            fullWidthUntil='md'
            key={radioField.value}
            onClick={() => {
              if (radioField.name.includes('contractHoursVacancyId')) {
                trackEvent(
                  selectWorkingHoursTrigger({
                    sub_feature: radioField.label.includes('-') ? 'range_hours' : 'single_hours'
                  })
                );
              } else {
                trackEvent(
                  selectAnswerTrigger({
                    question: label,
                    answer: radioField.value
                  })
                );
              }
              setValue(
                name,
                radioField.value === 'true' || radioField.value === 'false'
                  ? radioField.value === 'true'
                  : radioField.value
              );
              trigger(name);
            }}
            disabled={disable}
            className={watch(name)?.toString() === radioField.value ? 'selected' : ''}
            sx={{
              color: CbColorFontSubtle,
              fontWeight: CbFontWeightOpenSansNormal,
              border: `solid ${CbColorBorderBase} ${CbBorderWidth25}`,
              ':hover': {
                borderColor: CbColorBorderTertiary,
                backgroundColor: CbColorBackgroundInteractiveHovered
              },
              '&.selected': {
                borderColor: CbColorBorderInteractiveSelected,
                backgroundColor: CbColorBackgroundInteractiveHovered
              }
            }}
          >
            <Radio
              value={radioField.value}
              checked={watch(name)?.toString() === radioField.value ? true : false}
              {...register(
                name,
                radioFields[0].value === 'true' || radioFields[0].value === 'false'
                  ? ({
                      validate: value => value?.toString().length > 0 || rules?.required?.message
                    } as RegisterOptions<FieldValues, string>)
                  : { required: rules?.required }
              )}
              hidden
            />
            {radioField.label}
          </Button>
        ))}
      </Flex>
      {errorMessage && (
        <FormHelperText validation='error'>{errorMessage.toString()}</FormHelperText>
      )}
    </Flex>
  );
};
